import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, CardBody, Col, Row, Label, Input, Button, Spinner } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom'; 
import { toast } from 'react-toastify';
import {
  TreeView,
  processTreeViewItems,
  handleTreeViewCheckChange,
  TreeViewCheckDescriptor,
  TreeViewExpandChangeEvent,
} from '@progress/kendo-react-treeview';
import { get, postJson } from '../../../../hook/request';
import { Btn, H3 } from '../../../../AbstractElements';

type Group = { ID: number; nome: string };

type Modulo = {
  ID: number;
  parentID: number | string;
  pathfile: string | null;
  classe: string | null;
  ordine: number;
  abilitato: 0 | 1;
  codice: string;
  tipo: string;
  meta?: string | Record<string, any> | null;
  children?: Modulo[];
};

type KendoItem = { id: number; text: string; expanded?: boolean; items?: KendoItem[] };

function getNameFromMeta(meta: unknown, fallback = '-'): string {
  if (!meta) return fallback;
  try {
    const obj = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
    return obj?.it?.nome ?? obj?.IT?.nome ?? obj?.en?.nome ?? obj?.EN?.nome ?? fallback;
  } catch {
    return fallback;
  }
}
function toKendoTree(nodes: Modulo[] | undefined, expandRoots = true): KendoItem[] {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((n) => ({
    id: Number(n.ID),
    text: getNameFromMeta(n.meta, n.codice ?? String(n.ID)),
    expanded: expandRoots,
    items: toKendoTree(n.children ?? [], false),
  }));
}

export default function PermissionsEdit() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const slugId = useMemo(() => (slug ? Number(slug) : undefined), [slug]);
  const hydratedForGroup = useRef<number | null>(null);

  // Select gruppo
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<number | ''>('');
  const lockGroupSelect = !!slugId;

  // Dati del gruppo (facoltativo, per intestazione)
  const [groupInfo, setGroupInfo] = useState<any | null>(null);

  // Albero moduli
  const [rawTree, setRawTree] = useState<Modulo[]>([]);
  const kendoItems: KendoItem[] = useMemo(() => toKendoTree(rawTree, true), [rawTree]);

  // Stati UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Stato Tree (check & expand)
  const [treeState, setTreeState] = useState<{ check: TreeViewCheckDescriptor; items: KendoItem[] }>({
    check: { idField: 'id', applyCheckIndeterminate: true, ids: [] },
    items: [],
  });

  // ===== Caricamento iniziale gruppi + albero
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const [gRes, mRes] = await Promise.all([
          get<any>('/permissions/group?type=all'),
          get<any>('/configurazione_menu/list'),
        ]);

        const gList: Group[] = Array.isArray((gRes as any)?.data ?? gRes) ? ((gRes as any)?.data ?? gRes) : [];
        const tree: Modulo[] = Array.isArray((mRes as any)?.data ?? mRes) ? ((mRes as any)?.data ?? mRes) : [];

        setGroups(gList);
        setRawTree(tree);

        const items = toKendoTree(tree, true);
        setTreeState({
          items,
          check: { idField: 'id', applyCheckIndeterminate: true, ids: [] },
        });
        if (slugId) {
          setSelectedGroup(slugId);
        }
      } catch {
        toast.error('Errore nel caricamento dati.');
      } finally {
        setLoading(false);
      }
    })();
  }, [slugId]);

  // ===== Se ho uno slug, carico info del gruppo (facoltativo: serve solo per titolo/contesto)
  useEffect(() => {
    (async () => {
      if (!slugId) return;
      try {
        const r = await get<any>(`/permissions/group?edit=${slugId}`); // ✅ NEW
        const data = (r as any)?.data ?? r;
        setGroupInfo(data); // es: { ID, codice, ... , typeID, nomeType }
      } catch {
        // non blocco la pagina se fallisce
      }
    })();
  }, [slugId]);


  useEffect(() => {
    if (!selectedGroup) {
      // reset locale quando nessun gruppo è selezionato
      setTreeState(prev => ({ ...prev, check: { ...prev.check, ids: [] } }));
      hydratedForGroup.current = null;
      return;
    }
    if (treeState.items.length === 0) return;
    if (hydratedForGroup.current === selectedGroup) return;

    let aborted = false;
    (async () => {
      try {
        const res = await get<any>(`/permissions/group?edit=${selectedGroup}`);
        if (aborted) return;

        const payload = (res as any)?.data ?? res;
        const list: Array<{ ID: number }> = Array.isArray(payload) ? payload : payload ? [payload] : [];
        const ids = list.map(x => Number(x.ID)).filter(n => !Number.isNaN(n));

        setTreeState(prev => ({ ...prev, check: { ...prev.check, ids } }));
        hydratedForGroup.current = selectedGroup; // segna come idratato
      } catch {
        if (!aborted) toast.error('Impossibile caricare i permessi del gruppo.');
      }
    })();

    return () => { aborted = true; };
  }, [selectedGroup, treeState.items.length]);


  const onCheckChange = (event: any) => {
    const settings = { checkChildren: true, checkParents: true };
    const newCheck = handleTreeViewCheckChange(event, treeState.check, treeState.items, settings) as TreeViewCheckDescriptor;
    setTreeState(prev => ({ ...prev, check: { ...prev.check, ...newCheck } }));
  };
  // helper: clona e toggla expanded per id sull'albero SORGENTE
  function toggleExpandedById(items: KendoItem[], id: number): KendoItem[] {
    const walk = (arr: KendoItem[]): KendoItem[] =>
      arr.map(it => {
        if (it.id === id) {
          return { ...it, expanded: !it.expanded };
        }
        if (it.items && it.items.length) {
          return { ...it, items: walk(it.items) };
        }
        return it;
      });
    return walk(items);
  }


  // ✅ usa questo onExpandChange
  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    const id = (event.item as KendoItem).id;
    setTreeState(prev => ({
      ...prev,
      items: toggleExpandedById(prev.items, id),   // <-- aggiorni il sorgente
    }));
  };

    //creo la lista intera a partire dall padre
    function buildParentMap(nodes: Modulo[] | undefined) {
    const map = new Map<number, number>()
    const walk = (arr: Modulo[], parent?: number) => {
      for (const n of arr) {
        const id = Number(n.ID)
        const p = Number(n.parentID ?? parent ?? 0)
        if (!Number.isNaN(id)) map.set(id, Number.isFinite(p) ? p : 0)
        if (Array.isArray(n.children) && n.children.length) walk(n.children, id)
      }
    }
    if (Array.isArray(nodes)) walk(nodes, 0)
    return map
  }
  function withAncestors(ids: number[], parentMap: Map<number, number>) {
    const out = new Set<number>()
    for (const id of ids) {
      let cur = id
      while (cur && !out.has(cur)) {
        out.add(cur)
        const p = parentMap.get(cur) ?? 0
        if (!p || p === cur) break
        cur = p
      }
    }
    return Array.from(out)
  }

  const parentMap = useMemo(() => buildParentMap(rawTree), [rawTree])

  const onSave = async () => {
    if (!selectedGroup) {
      toast.warn('Seleziona un gruppo.')
      return
    }

    const checked = (treeState.check.ids as any[])
      .map(Number)
      .filter(n => !Number.isNaN(n))
    const permissions = withAncestors(checked, parentMap)

    const payload = {
      groupId: Number(selectedGroup),
      permissions,
    }

    try {
      setSaving(true)
      await postJson('/permissions/edit', payload)
      toast.success('Permessi aggiornati!')
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? 'Errore durante il salvataggio'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }


  if (loading) {
    return (
      <div className="page-body pt-4">
        <Card><CardBody className="d-flex align-items-center gap-2">
          <Spinner size="sm" /> <span>Caricamento…</span>
        </CardBody></Card>
      </div>
    );
  }

  return (
    <div className="page-body pt-4">
      <Col xxl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <H3 className="mb-0">
                Permessi per gruppo {groupInfo?.nomeType ? `– ${groupInfo.nomeType}` : ''}
              </H3>
              <Btn className="px-4" color="primary" type="button" onClick={() => navigate(-1)}>
                Torna Indietro
              </Btn>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Label>Gruppo</Label>
                <Input
                  type="select"
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value ? Number(e.target.value) : '')}
                  disabled={lockGroupSelect}
                >
                  <option value="">{groups.length ? 'Scegli gruppo…' : 'Nessun gruppo disponibile'}</option>
                  {groups.map((g) => (
                    <option key={g.ID} value={g.ID}>{g.nome}</option>
                  ))}
                </Input>
              </Col>
            </Row>

            <Row className="g-3 mt-3">
              <Col md={12}>
                <Label className="fw-semibold mb-2">Moduli</Label>
                <div className="border rounded p-2">
                  <TreeView
                    data={processTreeViewItems(treeState.items, { check: treeState.check })}
                    expandField="expanded"            // <-- importante
                    checkboxes
                    checkField="checked"
                    onCheckChange={onCheckChange}
                    expandIcons
                    onExpandChange={onExpandChange}
                  />

                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button color="success" onClick={onSave} disabled={saving || !selectedGroup}>
                {saving ? <Spinner size="sm" /> : 'Salva'}
              </Button>
            </div>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}
